// src/app/issue-detail/issue-detail.component.ts

import {
  Component,
  OnInit,
  ChangeDetectorRef,
  Inject,
  Optional,
  PLATFORM_ID,
  RESPONSE_INIT
} from '@angular/core';

import {
  ActivatedRoute,
  Router
} from '@angular/router';

import {
  isPlatformServer,
  CommonModule
} from '@angular/common';

import {
  IssuesService,
  Issue
} from '../services/issues.service';

import {
  SeoService
} from '../services/seo.service';

import {
  RouterModule
} from '@angular/router';

import {
  SocialShareComponent
} from '../components/social-share/social-share.component';

@Component({
  selector: 'app-issue-detail',
  standalone: true,
  templateUrl: './issue-detail.component.html',
  imports: [
    CommonModule,
    RouterModule,
    SocialShareComponent
  ],
  styleUrls: ['./issue-detail.component.css']
})
export class IssueDetailComponent implements OnInit {

  issue: Issue | null = null;
  relatedIssues: Issue[] = [];

  private readonly baseUrl =
    'https://www.lydialynnchristensen.com';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private issuesService: IssuesService,
    private seo: SeoService,
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: Object,
    @Optional()
    @Inject(RESPONSE_INIT)
    private responseInit: ResponseInit | null
  ) {}

  ngOnInit(): void {

    this.issuesService.fetchIssues();

    this.loadIssue();

    this.route.paramMap.subscribe(() => {
      this.loadIssue();
    });
  }

  loadIssue(): void {

    const idOrSlug =
      this.route.snapshot.paramMap.get('idOrSlug');

    if (!idOrSlug) {
      return;
    }

    this.issuesService.getIssues().subscribe({

      next: (issues: Issue[]) => {

        if (!issues || issues.length === 0) {
          return;
        }

        // Match by slug first.
        let matchedIssue =
          issues.find(
            issue => issue.slug === idOrSlug
          );

        let matchedByLegacyId = false;

        // Fall back to numeric ID for older URLs.
        if (!matchedIssue) {

          const numericId =
            Number(idOrSlug);

          if (!Number.isNaN(numericId)) {

            matchedIssue =
              issues.find(
                issue => issue.id === numericId
              );

            matchedByLegacyId = true;
          }
        }

        if (!matchedIssue) {
          return;
        }

        // Redirect old numeric URLs to the canonical slug URL.
        if (
          matchedByLegacyId &&
          matchedIssue.slug
        ) {

          const canonicalPath =
            `/issues/${matchedIssue.slug}`;

          if (
            isPlatformServer(this.platformId) &&
            this.responseInit
          ) {
            this.responseInit.status = 301;
          }

          this.router.navigate([
            canonicalPath
          ]);

          return;
        }

        this.issue = matchedIssue;

        // Resolve related issue IDs into full issue objects.
        this.relatedIssues =
          (matchedIssue.relatedIssues || [])
            .map(relatedId =>
              issues.find(
                issue => issue.id === relatedId
              )
            )
            .filter(
              (issue): issue is Issue => !!issue
            );

        // Clean the HTML description for metadata.
        const plainDescription =
          this.stripHtml(
            matchedIssue.description || ''
          );

        const plainSummary =
          matchedIssue.summary?.trim() ||
          plainDescription.slice(0, 155);

        // Use database SEO fields when available.
        const seoTitle =
          matchedIssue.seo_title?.trim() ||
          matchedIssue.title;

        const seoDescription =
          matchedIssue.seo_description?.trim() ||
          plainSummary;

        // Slug is canonical; numeric ID is the fallback.
        const canonicalSlug =
          matchedIssue.slug ||
          String(matchedIssue.id);

        const pageUrl =
          `${this.baseUrl}/issues/${canonicalSlug}`;

        const pageId =
          `${pageUrl}#webpage`;

        const policyId =
          `${pageUrl}#policy`;

        // Convert relative image URLs to absolute URLs.
        const absoluteImage =
          matchedIssue.image
            ? this.getAbsoluteUrl(
                matchedIssue.image
              )
            : undefined;

        // Update page SEO metadata and canonical URL.
        this.seo.update(
          seoTitle,
          seoDescription,
          `issues/${canonicalSlug}`,
          matchedIssue.image || ''
        );

        /*
         * The policy itself is represented as a DefinedTerm.
         * The actual page is represented as a WebPage.
         */

        const policyEntity: any = {

          "@type": "DefinedTerm",

          "@id": policyId,

          "name":
            matchedIssue.title,

          "description":
            plainDescription,

          "identifier":
            matchedIssue.slug ||
            String(matchedIssue.id),

          "subjectOf": {
            "@id": pageId
          }
        };

        if (absoluteImage) {

          policyEntity.image =
            absoluteImage;
        }

        this.seo.setStructuredData({

          "@context":
            "https://schema.org",

          "@graph": [

            {

              "@type": "WebPage",

              "@id": pageId,

              "url": pageUrl,

              "name":
                matchedIssue.title,

              "description":
                seoDescription,

              "isPartOf": {
                "@id": this.seo.websiteId
              },

              "about": {
                "@id": this.seo.lydiaId
              },

              "publisher": {
                "@id": this.seo.campaignId
              },

              "mainEntity": {
                "@id": policyId
              },

              "inLanguage": "en-US"
            },

            policyEntity

          ]

        });

        this.cdr.detectChanges();
      },

      error: (error) => {

        console.error(
          'Error fetching issues:',
          error
        );

      }

    });

  }

  private stripHtml(
    value: string
  ): string {

    return value
      .replace(/<[^>]*>/g, '')
      .replace(/\s+/g, ' ')
      .trim();

  }

  private getAbsoluteUrl(
    value: string
  ): string {

    if (
      value.startsWith('https://')
    ) {

      return value;

    }

    return (
      `${this.baseUrl}/${value.replace(/^\/+/, '')}`
    );

  }

  ngAfterViewChecked(): void {

    if (this.issue) {

      this.cdr.detectChanges();

    }

  }

}